// Uploading only the instances that CHANGED.
//
// `attr.needsUpdate = true` re-uploads the whole buffer. Highlighting a
// selection touches a handful of slots per mesh but pays for every slot the
// mesh can hold: a 40,000-element highlight spread over ~300 pooled meshes
// of capacity 512 re-uploads about 1.8 MB of instanceColor, every time the
// selection changes, to change a few thousand floats.
//
// three 0.164 takes `addUpdateRange(start, count)` in ARRAY ELEMENTS (not
// instances) and issues one bufferSubData per range. So the win is real but
// it is not free: each range is a driver call, and a few hundred tiny
// uploads cost more than one big one. Hence two rules below -
//   * merge runs separated by only a few slots (the bytes in the gap are
//     cheaper than another call), and
//   * give up and upload the whole buffer when the ranges would be too
//     many or would cover most of it anyway.
//
// THE TRAP, which is why `applyRanges` always owns `needsUpdate`: in
// WebGLAttributes.updateBuffer, a non-empty `updateRanges` SUPPRESSES the
// full upload. A range left pending on an attribute that some other code
// path then rewrites wholesale would upload the ranges and nothing else,
// leaving stale instances on the GPU that look exactly like a
// "hide didn't hide" bug. Ranges are therefore set and consumed in the
// same breath, and any full-buffer path must call clearUpdateRanges first.

// Slots closer than this are treated as one run: the wasted bytes are
// cheaper than a second bufferSubData.
export const MERGE_GAP_SLOTS = 8;
// More calls than this and the driver overhead beats the saving.
export const MAX_RANGES = 64;
// Covering more than this share of the buffer - upload it whole.
export const FULL_UPLOAD_SHARE = 0.34;

/**
 * Sorted, de-duplicated slot indices -> merged [start, endInclusive] runs.
 * Pure; exported for its own tests.
 */
export function mergeSlotRuns(indices, gap = MERGE_GAP_SLOTS) {
  if (!indices || indices.length === 0) return [];
  const sorted = [...new Set(indices)].sort((a, b) => a - b);
  const runs = [];
  let start = sorted[0];
  let end = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const v = sorted[i];
    if (v - end <= gap + 1) end = v;
    else { runs.push([start, end]); start = v; end = v; }
  }
  runs.push([start, end]);
  return runs;
}

/**
 * Decide how to upload `indices` on an attribute of `capacity` instances.
 * Returns { full: true } or { full: false, ranges: [{ start, count }] } in
 * ARRAY ELEMENTS, ready for addUpdateRange.
 */
export function uploadPlan(indices, capacity, itemSize, opts = {}) {
  const maxRanges = opts.maxRanges || MAX_RANGES;
  const share = opts.fullUploadShare || FULL_UPLOAD_SHARE;
  const gap = opts.gap === undefined ? MERGE_GAP_SLOTS : opts.gap;
  if (!indices || indices.length === 0) return { full: false, ranges: [] };
  if (!capacity || capacity <= 0 || !itemSize) return { full: true };

  const runs = mergeSlotRuns(indices, gap);
  if (runs.length > maxRanges) return { full: true };

  let covered = 0;
  for (const [a, b] of runs) covered += b - a + 1;
  if (covered >= capacity * share) return { full: true };

  return {
    full: false,
    ranges: runs.map(([a, b]) => ({ start: a * itemSize, count: (b - a + 1) * itemSize })),
  };
}

/**
 * Apply a plan to a BufferAttribute and mark it for upload. Always sets
 * needsUpdate, and always leaves the attribute in one of two clean states:
 * ranges pending, or no ranges at all.
 */
export function applyRanges(attr, indices, capacity, itemSize, opts) {
  if (!attr) return null;
  const plan = uploadPlan(indices, capacity, itemSize === undefined ? attr.itemSize : itemSize, opts);
  if (plan.full || !plan.ranges.length) {
    if (attr.clearUpdateRanges) attr.clearUpdateRanges();
    attr.needsUpdate = true;
    return plan.full ? 'full' : 'none';
  }
  for (const r of plan.ranges) {
    if (attr.addUpdateRange) attr.addUpdateRange(r.start, r.count);
    else { attr.needsUpdate = true; return 'full'; }   // older three: no range API
  }
  attr.needsUpdate = true;
  return 'ranges';
}

/** Bytes a plan uploads, for measurement and for the tests. */
export function planBytes(plan, capacity, itemSize, bytesPerElement = 4) {
  if (plan.full) return capacity * itemSize * bytesPerElement;
  return plan.ranges.reduce((n, r) => n + r.count * bytesPerElement, 0);
}
